import { Link } from 'react-router-dom'
import { ArrowLeft, ChevronRight } from 'lucide-react'

/**
 * <Page> — the content container. Audit: 11 different padding / max-width combos
 * (1100–1320px). Three widths, chosen by what the page holds, not by taste.
 *   width 'default' (1200px — lists, forms) | 'wide' (no cap — calendars, matrices)
 *         | 'narrow' (820px — self-service, single-column forms)
 */
const WIDTH = { default: 'max-w-[1200px]', wide: 'max-w-none', narrow: 'max-w-[820px]' }

export function Page({ width = 'default', className = '', children }) {
  return (
    <div className={`px-4 sm:px-6 lg:px-10 py-6 lg:py-8 ${WIDTH[width] || WIDTH.default} ${className}`}>
      {children}
    </div>
  )
}

/**
 * <PageHeader> — the top of every page. Audit: no shared header; titles came in
 * 22 / 21 / 20 / 19 / 16.5 / 15px, some pages had none, and the main button sat
 * top-right, in the filter row, inside a card or at the bottom.
 *
 * The rule it encodes:
 *   - title on the left (h1, 22px). Sub-pages inside a layout use level={2} (18px).
 *   - ONE primary action, top-right, in `actions`. Secondary actions go before it.
 *   - `back` = a link to the parent list (detail pages). `breadcrumb` = hub › page.
 *     Use one or the other, not both.
 *   - `subtitle` = one short sentence. Counts belong in `meta`.
 *   - `children` renders under the header (tabs, a status strip).
 */
export function PageHeader({ title, subtitle, meta, icon: Icon, back, breadcrumb, actions, level = 1, children, className = '' }) {
  const H = level === 1 ? 'h1' : 'h2'
  const titleCls = level === 1 ? 'text-[22px]' : 'text-[18px]'
  return (
    <header className={`mb-6 ${className}`}>
      {back && (
        <Link to={back.to} className="inline-flex items-center gap-1.5 text-[13.5px] text-slate-500 hover:text-brand-700 transition-colors mb-3">
          <ArrowLeft size={14} strokeWidth={2} />
          <span>{back.label}</span>
        </Link>
      )}
      {!back && breadcrumb?.length > 0 && (
        <nav aria-label="breadcrumb" className="flex items-center gap-1 text-[13px] text-slate-400 mb-2">
          {breadcrumb.map((b, i) => (
            <span key={i} className="inline-flex items-center gap-1">
              {i > 0 && <ChevronRight size={12} />}
              {b.to ? <Link to={b.to} className="hover:text-brand-700 transition-colors">{b.label}</Link> : <span className="text-slate-600">{b.label}</span>}
            </span>
          ))}
        </nav>
      )}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-3 min-w-0">
          {Icon && (
            <span className="flex-none w-10 h-10 rounded-xl bg-brand-50 text-brand-600 flex items-center justify-center">
              <Icon size={19} strokeWidth={2} />
            </span>
          )}
          <div className="min-w-0">
            <H className={`${titleCls} font-semibold text-slate-900 tracking-tight leading-tight`}>{title}</H>
            {subtitle && <p className="text-[14px] text-slate-500 mt-1">{subtitle}</p>}
            {meta && <div className="text-[13px] text-slate-500 mt-1">{meta}</div>}
          </div>
        </div>
        {actions && <div className="flex items-center gap-2 flex-wrap">{actions}</div>}
      </div>
      {children && <div className="mt-4">{children}</div>}
    </header>
  )
}
